import React, { useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Select,
  Typography,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { debounce } from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
// API
import { ADD_MY_CB_TALENT_ADMIN } from 'api/cbTalent/mutations';
import { GET_STORES_BY_NAME_AND_EMAIL } from 'api/store/queries';
// Types
import {
  AddMyCbTalentAdmin,
  AddMyCbTalentAdminVariables,
} from 'api/cbTalent/types/AddMyCbTalentAdmin';
import {
  GetStoresByNameAndEmail,
  GetStoresByNameAndEmailVariables,
  GetStoresByNameAndEmail_emailStores_entities,
  GetStoresByNameAndEmail_nameStores_entities,
} from 'api/store/types/GetStoresByNameAndEmail';
import { CbMyTalentSource } from 'api/graphql-global-types';
// UI
import { successNotification, errorNotification } from 'ui/Notification';

const { Text } = Typography;
const { Option, OptGroup } = Select;

type AddTalentModalProps = {
  agencyId: string;
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type FormValues = {
  userId: string;
  source?: CbMyTalentSource | null;
  firstWorkedAt?: moment.Moment | null;
  lastWorkedAt?: moment.Moment | null;
  lastRate?: number | null;
};

type StoreLike =
  | GetStoresByNameAndEmail_nameStores_entities
  | GetStoresByNameAndEmail_emailStores_entities
  | {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      storeDetails?: { storeName?: string | null } | null;
    };

const SOURCE_OPTIONS = Object.values(CbMyTalentSource).map((value) => ({
  label: value,
  value,
}));

const getStoreLabel = (store: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  storeDetails?: { storeName?: string | null } | null;
}): string => {
  const fullName = [store.firstName, store.lastName].filter(Boolean).join(' ');
  const storeName = store.storeDetails?.storeName;
  const primary = storeName || fullName || 'Unnamed talent';
  return store.email ? `${primary} (${store.email})` : primary;
};

const AddTalentModal: React.FC<AddTalentModalProps> = ({
  agencyId,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  const { data: storesData, refetch: refetchStores } = useQuery<
    GetStoresByNameAndEmail,
    GetStoresByNameAndEmailVariables
  >(GET_STORES_BY_NAME_AND_EMAIL, {
    variables: { searchTerm: '' },
    fetchPolicy: 'cache-and-network',
  });

  const [addTalent, { loading }] = useMutation<
    AddMyCbTalentAdmin,
    AddMyCbTalentAdminVariables
  >(ADD_MY_CB_TALENT_ADMIN);

  const handleSearchStores = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        refetchStores({ searchTerm: value });
      }, 400),
    [refetchStores]
  );

  const handleClose = (): void => {
    form.resetFields();
    setSearchTerm('');
    setPendingValues(null);
    onClose();
  };

  const handleSubmit = (values: FormValues): void => {
    setPendingValues(values);
  };

  const handleCancelConfirm = (): void => {
    setPendingValues(null);
  };

  const handleConfirmAdd = async (): Promise<void> => {
    if (!pendingValues) {
      return;
    }
    try {
      const input: AddMyCbTalentAdminVariables['input'] = {
        agencyId,
        userId: pendingValues.userId,
        source: pendingValues.source ?? null,
        firstWorkedAt: pendingValues.firstWorkedAt
          ? pendingValues.firstWorkedAt.toISOString()
          : null,
        lastWorkedAt: pendingValues.lastWorkedAt
          ? pendingValues.lastWorkedAt.toISOString()
          : null,
        lastRate:
          typeof pendingValues.lastRate === 'number'
            ? pendingValues.lastRate
            : null,
      };

      await addTalent({ variables: { input } });
      successNotification('Talent added successfully!');
      form.resetFields();
      setSearchTerm('');
      setPendingValues(null);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      errorNotification(
        err?.message || 'Failed to add talent, please try again later'
      );
      setPendingValues(null);
    }
  };

  const nameStores = storesData?.nameStores.entities || [];
  const emailStores = storesData?.emailStores.entities || [];
  const seenIds = new Set<string>();
  const dedupedEmailStores = emailStores.filter((store) => {
    if (seenIds.has(store.id) || nameStores.some((n) => n.id === store.id)) {
      return false;
    }
    seenIds.add(store.id);
    return true;
  });

  const allStores: StoreLike[] = [...nameStores, ...emailStores];
  const selectedStore = pendingValues
    ? allStores.find(
        (store) => (store as { id: string }).id === pendingValues.userId
      )
    : undefined;
  const selectedTalentLabel = selectedStore
    ? getStoreLabel(selectedStore)
    : 'this talent';

  return (
    <>
      <Modal
        title="Add Talent"
        visible={isVisible}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          name="addCbTalentForm"
          onFinish={handleSubmit}
          initialValues={{ source: CbMyTalentSource.Invited }}
        >
          <Form.Item
            name="userId"
            label={<Text>Talent</Text>}
            rules={[
              {
                required: true,
                message: 'Please select a talent',
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Search talent by name or email"
              filterOption={false}
              onSearch={handleSearchStores}
              notFoundContent={
                searchTerm ? 'No results' : 'Start typing to search'
              }
            >
              {nameStores.length > 0 && (
                <OptGroup label="By name">
                  {nameStores.map((store) => (
                    <Option key={`name_${store.id}`} value={store.id}>
                      {getStoreLabel(store)}
                    </Option>
                  ))}
                </OptGroup>
              )}
              {dedupedEmailStores.length > 0 && (
                <OptGroup label="By email">
                  {dedupedEmailStores.map((store) => (
                    <Option key={`email_${store.id}`} value={store.id}>
                      {getStoreLabel(store)}
                    </Option>
                  ))}
                </OptGroup>
              )}
            </Select>
          </Form.Item>

          <Form.Item name="source" label={<Text>Source</Text>}>
            <Select
              allowClear
              options={SOURCE_OPTIONS}
              placeholder="Select source"
            />
          </Form.Item>

          <Form.Item name="firstWorkedAt" label={<Text>First Worked At</Text>}>
            <DatePicker
              style={{ width: '100%' }}
              format="MM/DD/YYYY"
              placeholder="Select date"
            />
          </Form.Item>

          <Form.Item name="lastWorkedAt" label={<Text>Last Worked At</Text>}>
            <DatePicker
              style={{ width: '100%' }}
              format="MM/DD/YYYY"
              placeholder="Select date"
            />
          </Form.Item>

          <Form.Item name="lastRate" label={<Text>Last Rate</Text>}>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="Enter last rate"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ marginRight: 8 }}
            >
              Add
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <span>
            <ExclamationCircleOutlined
              style={{ color: '#faad14', marginRight: 8 }}
            />
            Add talent to My Talent CRM?
          </span>
        }
        visible={!!pendingValues}
        onCancel={handleCancelConfirm}
        onOk={handleConfirmAdd}
        okText="Yes, add talent"
        cancelText="Cancel"
        confirmLoading={loading}
        destroyOnClose
      >
        <Text>
          Are you sure you want to add <strong>{selectedTalentLabel}</strong> to
          your My Talent CRM?
        </Text>
      </Modal>
    </>
  );
};

export default AddTalentModal;
